import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { PageWrapper } from "components/ui/PageWrapper";
import { Button } from "components/ui/Button";
import { ComponentLoader } from "components/Loader/ComponentLoader";
import { ErrorComponent } from "components/Alerts/Error";
import { InfoComponent } from "components/Alerts/Info";
import { getIconComponent } from "components/ui/IconSelector";
import { 
  GetPartnerServices, 
  DeletePartnerService as DeletePartnerServiceMutation, 
  TogglePartnerServiceStatus as TogglePartnerServiceStatusMutation 
} from "rest/partnerServices";

import DataTable from "react-data-table-component";
import DataTableExtensions from "react-data-table-component-extensions";
import { tableCustomStyles } from "app/mock/catalog";
import { Edit2, Trash2, Eye, EyeOff, Plus } from "lucide-react";

import "styles/main.css";
import "react-data-table-component-extensions/dist/index.css";

export default function PartnerServices() {
  const navigate = useNavigate();
  const [selectedId, setSelectedId] = useState(null);

  // API calls
  const allServices = GetPartnerServices();
  const deleteServiceMutation = DeletePartnerServiceMutation();
  const toggleStatusMutation = TogglePartnerServiceStatusMutation();

  // Handle delete
  const handleDelete = (id) => {
    if (window.confirm("Are you sure you want to delete this partner service?")) {
      setSelectedId(id);
      deleteServiceMutation.mutate(id, {
        onSettled: () => setSelectedId(null)
      });
    }
  };

  // Handle status toggle
  const handleStatusToggle = (id, currentStatus) => {
    const newStatus = currentStatus === 'active' ? 'inactive' : 'active';
    setSelectedId(id);
    toggleStatusMutation.mutate(
      { id, status: newStatus },
      {
        onSettled: () => setSelectedId(null)
      }
    );
  };

  // Table columns configuration
  const columns = [
    {
      name: "Order",
      selector: (row) => row.displayOrder,
      sortable: true,
      width: "100px",
    },
    {
      name: "Icon",
      cell: (row) => {
        const IconComponent = getIconComponent(row.icon);
        return IconComponent ? (
          <div style={{ padding: "8px" }}>
            <IconComponent size={20} />
          </div>
        ) : (
          <span style={{ color: "#6c757d", fontSize: "12px" }}>No Icon</span>
        );
      },
      width: "80px",
    },
    {
      name: "Title",
      selector: (row) => row.title,
      sortable: true,
      minWidth: "200px",
    },
    {
      name: "Content Preview",
      cell: (row) => {
        if (row.contentType === 'description') {
          return (
            <div style={{ maxWidth: "250px" }}>
              <p style={{ 
                fontSize: "12px", 
                margin: 0, 
                overflow: "hidden", 
                textOverflow: "ellipsis",
                whiteSpace: "nowrap"
              }}>
                {row.description}
              </p>
            </div>
          );
        } else {
          const items = typeof row.items === 'string' 
            ? row.items.split('@').filter(item => item.trim()) 
            : row.items || [];
          return (
            <div style={{ maxWidth: "250px" }}>
              <small style={{ color: "#6c757d" }}>
                {items.length} items: {items.slice(0, 2).join(", ")}
                {items.length > 2 && "..."}
              </small>
            </div>
          );
        }
      },
      minWidth: "250px",
    },
    {
      name: "Link",
      cell: (row) => (
        <div style={{ fontSize: "12px" }}>
          <div>{row.linkText || "View more"}</div>
          <div style={{ color: "#6c757d" }}>{row.linkUrl}</div>
        </div>
      ),
      minWidth: "150px",
    },
    {
      name: "Status",
      cell: (row) => (
        <button
          type="button"
          onClick={() => handleStatusToggle(row.id, row.status)}
          className={`btn btn-sm ${row.status === 'active' ? 'btn-success' : 'btn-secondary'}`}
          disabled={selectedId === row.id && toggleStatusMutation.isPending}
        >
          {selectedId === row.id && toggleStatusMutation.isPending ? (
            <span className="spinner-border spinner-border-sm" role="status" />
          ) : (
            <>
              {row.status === 'active' ? <Eye size={14} /> : <EyeOff size={14} />}
              <span className="ms-1">{row.status === 'active' ? 'Active' : 'Inactive'}</span>
            </>
          )}
        </button>
      ),
      width: "130px",
    },
    {
      name: "Actions",
      cell: (row) => (
        <div className="d-flex gap-2">
          <button
            type="button"
            onClick={() => navigate(`/edit-partner-service/${row.id}`)}
            className="btn btn-primary btn-sm"
            title="Edit"
          >
            <Edit2 size={14} />
          </button>
          <button
            type="button"
            onClick={() => handleDelete(row.id)}
            className="btn btn-danger btn-sm"
            disabled={selectedId === row.id && deleteServiceMutation.isPending}
            title="Delete"
          >
            {selectedId === row.id && deleteServiceMutation.isPending ? (
              <span className="spinner-border spinner-border-sm" role="status" />
            ) : (
              <Trash2 size={14} />
            )}
          </button>
        </div>
      ),
      width: "120px",
      ignoreRowClick: true,
    },
  ];

  // Transform data for table
  const transformedData = (allServices?.data?.data || []).map(service => ({
    ...service,
    displayOrder: service.display_order,
    contentType: service.content_type,
    linkText: service.link_text,
    linkUrl: service.link_url,
    // Database returns ENUM('active', 'inactive') directly, so use as-is
    status: service.status,
    description: service.description || '',
    items: service.items ? service.items.split('@').filter(item => item.trim()) : []
  }));

  // Data for table
  const tableData = {
    columns,
    data: transformedData,
  };

  // Loading state
  if (allServices?.isLoading) {
    return <ComponentLoader />;
  }

  // Error state
  if (allServices?.isError) {
    return (
      <ErrorComponent 
        message="Failed to load partner services. Please try again."
        onRetry={() => allServices.refetch()}
      />
    );
  }

  return (
    <div className="partner-services-page">
      <PageWrapper 
        slug="partner-services" 
        name="Services"
        breadcrumbItems={[
          { label: "Dashboard", link: "/" },
          { label: "Services", link: "/partner-services" }
        ]}
      />
      
      <div className="page-content">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h4 className="page-title">Services Management</h4>
          <div className="add_catalog_btn">
            <button
              onClick={() => navigate("/add-partner-service")}
              className="btn btn-primary d-flex align-items-center gap-2"
              style={{ 
                backgroundColor: "#2a3071",
                borderColor: "#2a3071",
                width: "180px !important",
                whiteSpace: "nowrap"
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = "#34caff";
                e.target.style.borderColor = "#34caff";
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = "#2a3071";
                e.target.style.borderColor = "#2a3071";
              }}
            >
              <Plus size={16} />
              Add New Service
            </button>
          </div>
        </div>

        {/* Data Table */}
        <div className="card">
          <div className="card-body">
            {allServices?.data?.data && allServices.data.data.length > 0 ? (
              <DataTableExtensions {...tableData} exportHeaders>
                <DataTable
                  columns={columns}
                  data={allServices.data.data}
                  pagination
                  paginationPerPage={10}
                  paginationRowsPerPageOptions={[5, 10, 15, 20]}
                  highlightOnHover
                  striped
                  responsive
                  customStyles={tableCustomStyles}
                  defaultSortFieldId={1} // Sort by display order by default
                />
              </DataTableExtensions>
            ) : (
              <div className="text-center py-4">
                <p className="mb-3">No partner services found.</p>
                <div className="add_catalog_btn mx-auto d-inline-block">
                  <button
                    onClick={() => navigate("/add-partner-service")}
                    className="btn btn-primary d-flex align-items-center gap-2"
                    style={{ 
                      backgroundColor: "#2a3071",
                      borderColor: "#2a3071",
                      width: "180px !important",
                      whiteSpace: "nowrap"
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = "#34caff";
                      e.target.style.borderColor = "#34caff";
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = "#2a3071";
                      e.target.style.borderColor = "#2a3071";
                    }}
                  >
                    <Plus size={16} />
                    Add First Service
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}